import styled, { keyframes } from 'styled-components';

import theme from '../../../assets/theme';

/**
 * Shared styled primitives for the new auth experience. Tokens lifted from the
 * Curipod design system (assets/theme.ts) to match the handoff design 1:1.
 */

const ease = 'cubic-bezier(.645,.045,.355,1)';
const textFaded = 'rgba(40,47,51,.5)';
const textQuiet = 'rgba(40,47,51,.38)';
const greyVeryLight = '#f5f5f5';
export const shadowCard =
  '0 24px 64px -20px rgba(40,47,51,.22), 0 4px 16px -8px rgba(40,47,51,.12)';
export const shadowFloat = '0 1.6px 3.6px rgba(0,0,0,.13), 0 .3px .9px rgba(0,0,0,.11)';

const hintIn = keyframes`
  from { opacity: 0; transform: translateY(-3px); }
  to { opacity: 1; transform: none; }
`;

export const rise = keyframes`
  from { transform: translateY(10px); }
  to { transform: none; }
`;

export const AuthHead = styled.div`
  margin-bottom: 24px;
  text-align: center;
`;

export const AuthTitle = styled.h1`
  margin: 0;
  font-size: 31px;
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: ${theme.colors.black};
`;

export const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 800;
  color: ${theme.colors.greyDark};
  margin-bottom: 6px;
`;

export const Field = styled.input<{ $err?: boolean }>`
  width: 100%;
  padding: 13px 14px;
  border-radius: 10px;
  border: 1.5px solid ${({ $err }) => ($err ? theme.colors.red : theme.colors.grey)};
  background: #fff;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.black};
  transition: border-color 0.18s ${ease}, box-shadow 0.18s ${ease};

  &::placeholder {
    color: ${textQuiet};
    font-weight: 700;
  }
  &:focus {
    outline: none;
    border-color: ${theme.colors.orange};
    box-shadow: 0 0 0 3px rgba(249, 151, 70, 0.16);
  }
  ${({ $err }) => $err && `box-shadow: 0 0 0 3px rgba(216,76,66,.14);`}
`;

export const FieldErr = styled.div`
  margin-top: 6px;
  font-size: 12.5px;
  font-weight: 700;
  color: ${theme.colors.red};
`;

export const SupportRef = styled.div`
  margin-top: 4px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${theme.colors.text.grey};

  code {
    font-family: monospace;
    font-weight: 700;
    color: ${theme.colors.black};
    word-break: break-all;
  }
`;

export const FieldHint = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: flex-start;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 700;
  color: ${textFaded};
  line-height: 1.45;
  animation: ${hintIn} 0.22s ${ease};

  svg {
    width: 15px;
    height: 15px;
    flex: 0 0 auto;
    margin-top: 1px;
    color: ${theme.colors.orange};
    opacity: 0.95;
  }
`;

export const Btn = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 11px;
  font-family: inherit;
  font-size: 15.5px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  border: none;
  transition: all 0.2s ${ease};

  svg.ico {
    width: 20px;
    height: 20px;
  }
`;

export const PrimaryBtn = styled(Btn)`
  background: ${theme.colors.orange};
  color: #fff;
  box-shadow: 0 1px 2px rgba(40, 47, 51, 0.12);

  &:hover:not(:disabled) {
    background: ${theme.colors.orangeDark};
  }
  &:disabled {
    background: ${theme.colors.grey};
    color: #fff;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const OAuthBtn = styled(Btn)`
  background: #fff;
  color: ${theme.colors.black};
  border: 1.5px solid ${theme.colors.grey};

  &:hover {
    border-color: ${theme.colors.iconGrey};
    background: ${greyVeryLight};
  }
`;

export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 18px 0;
  color: ${textQuiet};
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.grey};
  }
`;

export const OAuthTiles = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols ?? 3}, 1fr);
  gap: 8px;

  &.more-tiles {
    margin-top: 8px;
    animation: ${hintIn} 0.22s ${ease};
  }

  /* On narrow phones, stack the providers full-width, one per row. */
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

export const OAuthTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  padding: 14px 4px;
  border-radius: 12px;
  background: #fff;
  border: 1.5px solid ${theme.colors.grey};
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${theme.colors.black};
  cursor: pointer;
  transition: all 0.2s ${ease};

  svg.ico {
    width: 25px;
    height: 25px;
  }
  &:hover {
    border-color: ${theme.colors.iconGrey};
    background: ${greyVeryLight};
    transform: translateY(-1px);
  }

  /* Full-width row layout (icon + label inline) when stacked on narrow phones. */
  @media (max-width: 500px) {
    flex-direction: row;
    justify-content: center;
    gap: 10px;
    padding: 14px 16px;
    font-size: 14px;
  }
`;

export const OtherProvidersLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  margin-top: 14px;
  padding: 4px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 800;
  color: ${textFaded};
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ${ease};

  &:hover {
    color: ${theme.colors.orangeDark};
  }
  .chev {
    width: 15px;
    height: 15px;
    transition: transform 0.2s ${ease};
  }
  .chev.up {
    transform: rotate(180deg);
  }
`;

export const SwitchLine = styled.div`
  margin-top: 22px;
  text-align: center;
  font-size: 14.5px;
  font-weight: 700;
  color: ${textFaded};

  button {
    color: ${theme.colors.orangeDark};
    font-weight: 800;
    padding: 0;
    font-size: inherit;
    background: none;
    border: none;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const Legal = styled.div`
  margin-top: 18px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
  color: ${textQuiet};
  line-height: 1.55;

  a {
    color: ${textFaded};
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover {
      color: ${theme.colors.black};
    }
  }
`;

export const LinkBtn = styled.button`
  color: ${theme.colors.greyDark};
  font-weight: 800;
  font-size: 14px;
  font-family: inherit;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 15px;
    height: 15px;
  }
  &:hover {
    color: ${theme.colors.orangeDark};
  }
`;

export const StepBack = styled.div`
  margin-top: 16px;
  text-align: center;
`;

/* ---- code / join boxes ---- */
export const CodeBoxes = styled.div`
  display: flex;
  justify-content: center;
  gap: 9px;
  margin: 26px 0 8px;

  input {
    width: 46px;
    height: 58px;
    text-align: center;
    font-family: inherit;
    font-size: 26px;
    font-weight: 900;
    color: ${theme.colors.black};
    border: 1.5px solid ${theme.colors.grey};
    border-radius: 12px;
    background: #fff;
    transition: all 0.15s ${ease};
  }
  input:focus {
    outline: none;
    border-color: ${theme.colors.orange};
    box-shadow: 0 0 0 3px rgba(249, 151, 70, 0.18);
  }
  input.filled {
    border-color: ${theme.colors.orange};
    background: #fff8f1;
  }

  @media (max-width: 380px) {
    gap: 6px;
    input {
      width: 40px;
      height: 52px;
      font-size: 22px;
    }
  }
`;

export const CenterWrap = styled.div`
  text-align: center;

  h2 {
    font-size: 27px;
    font-weight: 900;
    letter-spacing: -0.02em;
    color: ${theme.colors.black};
  }
  p {
    margin-top: 10px;
    font-size: 15px;
    font-weight: 700;
    color: ${textFaded};
    line-height: 1.55;
  }
  .email-em {
    color: ${theme.colors.black};
    font-weight: 800;
  }
`;

export const Sticker = styled.img`
  width: 96px;
  height: 96px;
  margin: 0 auto 18px;
  display: block;
`;

export const ConfirmActions = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/* Full-viewport beige wash shown while the sign-in code is being verified — it
   covers the logo and card so only the loader is visible, matching the app's
   main loader the user lands on next. */
export const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.background};
`;
